import ContentSave from '@mui/icons-material/Save';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { FormEvent, useEffect, useState } from 'react';
import { Toolbar } from 'react-admin';
import { DialogCloseButton } from '@app/misc/DialogCloseButton';
import { Tag } from '@wwf/sdk';
import { colors } from './colors';
import { RoundButton } from './RoundButton';

type TagDialogProps = {
    open: boolean;
    tag?: Pick<Tag, 'name' | 'color'>;
    title: string;
    onSubmit(tag: Pick<Tag, 'name' | 'color'>): Promise<void>;
    onClose(): void;
};

export function TagDialog({
    open,
    tag,
    title,
    onClose,
    onSubmit,
}: TagDialogProps) {
    const [newTagName, setNewTagName] = useState('');
    const [newTagColor, setNewTagColor] = useState(colors[0]);
    const [disabled, setDisabled] = useState(false);

    const handleNewTagNameChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setNewTagName(event.target.value);
    };

    const handleNewTagColorChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setNewTagColor(event.target.value);
    };

    const handleClose = () => {
        setDisabled(false);
        onClose();
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        await onSubmit({ name: newTagName, color: newTagColor });

        setDisabled(true);
        setNewTagName('');
        setNewTagColor(colors[0]);

        handleClose();
    };

    useEffect(() => {
        setNewTagName(tag?.name ?? '');
        setNewTagColor(tag?.color ?? colors[0]);
    }, [tag]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="form-dialog-title"
        >
            <form onSubmit={handleSubmit}>
                <DialogCloseButton onClose={handleClose} />
                <DialogTitle id="form-dialog-title">{title}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        label="Tag name"
                        value={newTagName}
                        onChange={handleNewTagNameChange}
                        sx={{ mt: 1 }}
                    />
                    <Box display="flex" flexWrap="wrap" width={230} mt={2}>
                        {colors.map(color => (
                            <RoundButton
                                key={color}
                                color={color}
                                selected={color === newTagColor}
                                handleClick={() => {
                                    setNewTagColor(color);
                                }}
                            />
                        ))}
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5} mt={2}>
                        <Typography
                            variant="body2"
                            component="label"
                            htmlFor="tag-color-picker"
                        >
                            Custom color
                        </Typography>
                        <Box
                            component="input"
                            id="tag-color-picker"
                            type="color"
                            value={newTagColor}
                            onChange={handleNewTagColorChange}
                            sx={{
                                width: 30,
                                height: 30,
                                p: 0,
                                border: '2px solid',
                                borderColor: 'grey.400',
                                borderRadius: '50%',
                                cursor: 'pointer',
                                bgcolor: 'transparent',
                                '&::-webkit-color-swatch-wrapper': {
                                    padding: 0,
                                },
                                '&::-webkit-color-swatch': {
                                    border: 'none',
                                    borderRadius: '50%',
                                },
                                '&::-moz-color-swatch': {
                                    border: 'none',
                                    borderRadius: '50%',
                                },
                            }}
                        />
                        <TextField
                            size="small"
                            value={newTagColor}
                            onChange={handleNewTagColorChange}
                            inputProps={{
                                'aria-label': 'Tag color hex value',
                            }}
                            sx={{ width: 100 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        justifyContent: 'flex-start',
                        p: 0,
                    }}
                >
                    <Toolbar
                        sx={{
                            width: '100%',
                        }}
                    >
                        <Button
                            type="submit"
                            color="primary"
                            disabled={disabled}
                            variant="contained"
                            startIcon={<ContentSave />}
                        >
                            Save
                        </Button>
                    </Toolbar>
                </DialogActions>
            </form>
        </Dialog>
    );
}
